'use client';

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { ChevronDown } from '@modules/common/icons';

import { Fragment } from 'react';

export type SortOptions =
  | 'popular'
  | 'title_asc'
  | 'title_desc'
  | 'price_asc'
  | 'price_desc';

type SortProductsProps = {
  sortBy: SortOptions;
  setQueryParams: (name: string, value: SortOptions) => void;
  'data-testid'?: string;
};

const sortOptions: { value: SortOptions; label: string }[] = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'title_asc', label: 'Name: A to Z' },
  { value: 'title_desc', label: 'Name: Z to A' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
];

const SortProducts = ({
  'data-testid': dataTestId,
  sortBy,
  setQueryParams,
}: SortProductsProps) => {
  const selectedOption =
    sortOptions.find((o) => o.value === sortBy) || sortOptions[0];

  return (
    <div className="relative" data-testid={dataTestId}>
      <Listbox
        value={sortBy}
        onChange={(value: SortOptions) => setQueryParams('sortBy', value)}
      >
        <ListboxButton className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-fg-neutral-strong border border-border-semantic-subtle rounded-base bg-white hover:border-border-semantic transition-colors">
          <span>Sort: {selectedOption.label}</span>
          <ChevronDown
            size="14"
            className="text-fg-neutral-soft"
            aria-hidden="true"
          />
        </ListboxButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <ListboxOptions
            anchor={{ to: 'bottom end', gap: 4 }}
            className="z-10 w-56 origin-top bg-white border border-border-semantic-subtle rounded-base shadow-lg focus:outline-hidden [--anchor-max-height:80vh]"
          >
            {sortOptions.map((option) => (
              <ListboxOption
                key={option.value}
                value={option.value}
                className={({ active, selected }) => {
                  let style = 'text-fg-neutral-subtle';
                  if (selected) {
                    style =
                      'text-fg-interactive-strong font-medium bg-surface-neutral-default';
                  } else if (active) {
                    style = 'text-fg-neutral-strong bg-surface-neutral-default';
                  }
                  return `cursor-pointer px-4 py-2.5 text-sm ${style}`;
                }}
              >
                {option.label}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Transition>
      </Listbox>
    </div>
  );
};

export default SortProducts;
